/**
 * Excel export for derivative recon results.
 *
 * Mirrors recon-dashboard/api/recon_export.py sheet for sheet: same tabs, same
 * column order, same sorting, same number/date formats and autofilters, so the
 * workbook this app produces is interchangeable with the one that repo emits.
 */
import * as XLSX from 'xlsx';

import { isDateCol, isNumCol, toDateObj } from './derivativeReconFormat';
import { wireIdsFor } from './derivativeReconLogic';

const STATUS_LABELS = {
  MATCHED: 'Matched',
  VAL_BREAK: 'Value Break',
  QTY_BREAK: 'Position Qty Break',
  MISSING_CP: 'Missing from Milliman',
  MISSING_BOR: 'Missing from Clearwater',
};

// Cash Rec reuses the same status codes with different labels — MISSING_BOR
// and MISSING_CP mean the opposite side here. See derivativeReconConstants.
const CASH_STATUS_LABELS = {
  MATCHED: 'Matched',
  VAL_BREAK: 'Amount Break',
  AMBIGUOUS: 'Ambiguous Match',
  MISSING_BOR: 'Missing from Custody',
  MISSING_CP: 'Unmatched in Custody',
};

const NUM_FMT = '#,##0.00';
const DATE_FMT = 'm/d/yyyy';

/** Fast path for the two date shapes recon results carry: M/D/YYYY, YYYY-MM-DD. */
function parseMdY(v) {
  if (v == null || v === '') return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const s = String(v).trim();
  if (!s) return null;
  if (s.includes('/')) {
    const p = s.split('/');
    if (p.length === 3) {
      const m = parseInt(p[0], 10);
      const d = parseInt(p[1], 10);
      let y = parseInt(p[2], 10);
      if (!isNaN(m) && !isNaN(d) && !isNaN(y)) {
        if (y < 100) y += 2000;
        return new Date(y, m - 1, d);
      }
    }
  }
  if (s.includes('-')) {
    const head = s.split('T')[0].split(' ')[0];
    const p = head.split('-');
    if (p.length === 3) {
      const y = parseInt(p[0], 10);
      const m = parseInt(p[1], 10);
      const d = parseInt(p[2].slice(0, 2), 10);
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) return new Date(y, m - 1, d);
    }
  }
  return toDateObj(s);
}

const dateKey = (v) => {
  const d = parseMdY(v);
  return d ? d.getTime() : -1;
};

/** Stable descending sort by a date-ish field, matching Python's sorted(reverse=True). */
const sortByDateDesc = (rows, field) =>
  [...rows].sort((a, b) => dateKey(b[field]) - dateKey(a[field]));

/**
 * Build a worksheet from a header row, value rows and per-column formats.
 * Formats are applied only when the value really is of that type, so a stray
 * string in a numeric column stays readable instead of rendering as 0.00.
 */
function makeSheet(headers, rows, formats) {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows], { cellDates: true });
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let r = 1; r <= range.e.r; r += 1) {
    for (let c = 0; c <= range.e.c; c += 1) {
      const fmt = formats[c];
      if (!fmt) continue;
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (!cell) continue;
      if (fmt === NUM_FMT && cell.t === 'n') cell.z = NUM_FMT;
      else if (fmt === DATE_FMT && cell.t === 'd') cell.z = DATE_FMT;
    }
  }
  ws['!cols'] = headers.map((h) => ({ wch: Math.max(String(h).length, 14) }));
  if (headers.length && rows.length) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range(
        { r: 0, c: 0 },
        { r: rows.length, c: headers.length - 1 },
      ),
    };
  }
  return ws;
}

const N = NUM_FMT;
const D = DATE_FMT;

/**
 * A cell value for a date column: a real Date when the value parses, so Excel
 * filters and date arithmetic work, and the original text when it does not.
 * Blank stays blank rather than becoming the epoch.
 */
const dateCell = (v) => parseMdY(v) || (v == null ? '' : v);

function writePositions(wb, posResults) {
  const headers = [
    'Trade ID', 'CW Raw ID', 'Entity', 'Broker',
    'Mil Notional', 'CW Notional', 'Notional Diff',
    'Mil # Options', 'CW Units', 'Qty Diff',
    'Mil Strike', 'CW Strike', 'Strike Diff',
    'Mil Trade Date', 'CW Trade Date', 'Mil Settle Date', 'CW Settle Date', 'Status',
  ];
  const formats = [null, null, null, null, N, N, N, N, N, N, N, N, N, D, D, D, D, null];
  const rows = sortByDateDesc(posResults, 'milTrade').map((r) => [
    r.id, r.rawCwId || '', r.entity || '', r.cpName,
    r.milVal, r.cwVal, r.valDiff,
    r.milQty, r.cwQty, r.qtyDiff,
    r.milStrike, r.cwStrike, r.strikeDiff,
    dateCell(r.milTrade), dateCell(r.cwTrade), dateCell(r.milSettle), dateCell(r.cwSettle),
    STATUS_LABELS[r.status] || r.status || '',
  ]);
  XLSX.utils.book_append_sheet(wb, makeSheet(headers, rows, formats), 'Positions');
}

// Strictly `${flowType}|${tradeId}`. A trade normally has both a premium and a
// payoff, so falling back to the other type puts the wire that settled one
// flow on the row for the other — a settled-looking amount against a break.
// The result is a list: flows are keyed per settlement date, so one trade of
// one type can ride several wires.
function wiresForTrade(flowType, tradeId, wires, tradeIdx) {
  return wireIdsFor(tradeIdx, flowType, tradeId).map((wid) => wires[wid]).filter(Boolean);
}

function writeTx(wb, label, rows, flowType, wires, tradeIdx) {
  const headers = [
    'Trade ID', 'Entity', 'Broker', 'Settle Date',
    'Mil Total', 'CW Total', 'Diff ($)',
    'Wire Net (custody)', 'Wire Date', 'Wire Account', 'Status',
  ];
  const formats = [null, null, null, D, N, N, N, N, D, null, null];
  const body = sortByDateDesc(rows, 'settle').map((r) => {
    const tradeWires = wiresForTrade(flowType, r.id, wires, tradeIdx);
    // One wire keeps its own date cell; several are listed, since no single
    // date describes them and a sum of dates means nothing.
    const wireDate = tradeWires.length === 1
      ? dateCell(tradeWires[0].dateStr)
      : tradeWires.map((w) => w.dateStr || '').filter(Boolean).join(' / ');
    return [
      r.id, r.entity || '', r.cpName, dateCell(r.settle),
      r.milTotal, r.cwTotal, r.diff,
      tradeWires.length
        ? Math.abs(tradeWires.reduce((acc, w) => acc + (w.amt || 0), 0))
        : null,
      wireDate,
      tradeWires.map((w) => w.acctNum || '').filter(Boolean).join(' / '),
      STATUS_LABELS[r.status] || r.status || '',
    ];
  });
  XLSX.utils.book_append_sheet(wb, makeSheet(headers, body, formats), label);
}

const extractEntityForExport = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  const i = s.indexOf('-');
  return i >= 0 ? s.slice(i + 1).trim() : s;
};

function writeCashRec(wb, rows) {
  if (!rows || !rows.length) return;
  const headers = [
    'Type', 'Broker', 'Entity', 'Mil Date', 'Mil Amount',
    'Custody Date', 'Custody Amount', 'Source', 'Description',
    'Diff ($)', 'Status', 'Trade IDs',
  ];
  const formats = [null, null, null, D, N, D, N, null, null, N, null, null];
  const fmtAmt = (n) => Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  const sorted = [...rows].sort(
    (a, b) => dateKey((b.mil || {}).dateStr) - dateKey((a.mil || {}).dateStr),
  );
  const body = sorted.map((r) => {
    const mil = r.mil || {};
    const custody = r.custody;
    let custDate = '';
    let custAmt = null;
    let source = '';
    let desc = '';
    if (r.ambiguous && Array.isArray(custody)) {
      custDate = custody.map((c) => String(c.dateStr || '')).join(' / ');
      custAmt = custody.map((c) => fmtAmt(Math.abs(c.amt || 0))).join(' / ');
      source = custody.map((c) => String(c.source || '')).join(' / ');
      desc = custody.map((c) => String(c.desc || '')).join(' | ');
    } else if (custody && !Array.isArray(custody)) {
      custDate = dateCell(custody.dateStr);
      custAmt = custody.amt != null ? Math.abs(custody.amt) : null;
      source = custody.source || '';
      desc = custody.desc || '';
    }
    return [
      mil.type || '', mil.broker || '', extractEntityForExport(mil.entity),
      dateCell(mil.dateStr), mil.total != null ? Math.abs(mil.total) : null,
      custDate, custAmt, source, desc,
      r.diff, CASH_STATUS_LABELS[r.status] || r.status || '',
      (mil.tradeIds || []).join(', '),
    ];
  });
  XLSX.utils.book_append_sheet(wb, makeSheet(headers, body, formats), 'Cash Rec');
}

function coerceCell(col, v) {
  if (v == null || v === '') return null;
  if (isDateCol(col)) return parseMdY(v) || v;
  if (isNumCol(col)) {
    const n = parseFloat(String(v).replace(/,/g, ''));
    return isNaN(n) ? v : n;
  }
  return v;
}

function writeRaw(wb, label, rows) {
  if (!rows || !rows.length) return;
  const cols = Object.keys(rows[0]);
  const sortField = cols.find((c) => c.toLowerCase().includes('trade date'))
    || cols.find((c) => isDateCol(c))
    || null;
  const sorted = sortField
    ? [...rows].sort((a, b) => {
      const ka = a[sortField] instanceof Date ? a[sortField].getTime() : dateKey(a[sortField]);
      const kb = b[sortField] instanceof Date ? b[sortField].getTime() : dateKey(b[sortField]);
      return kb - ka;
    })
    : rows;
  const formats = cols.map((c) => {
    if (isDateCol(c)) return DATE_FMT;
    if (isNumCol(c)) return NUM_FMT;
    return null;
  });
  const body = sorted.map((r) => cols.map((c) => coerceCell(c, r[c])));
  XLSX.utils.book_append_sheet(wb, makeSheet(cols, body, formats), label.slice(0, 31));
}

/** Build the recon workbook. Exported for tests. */
export function buildDerivativeReconWorkbook(recon) {
  const r = recon || {};
  const wb = XLSX.utils.book_new();
  const wires = r.wires || {};
  const tradeIdx = r.tradeWireIndex || {};
  writePositions(wb, r.posResults || []);
  writeTx(wb, 'Premiums', r.premResults || [], 'Premium', wires, tradeIdx);
  writeTx(wb, 'Payoffs', r.payResults || [], 'Payoff', wires, tradeIdx);
  writeCashRec(wb, r.cashResults || []);
  writeRaw(wb, 'Milliman Positions', r.milRaw || []);
  writeRaw(wb, 'Milliman Payoffs', r.milPayRaw || []);
  writeRaw(wb, 'CW Positions', r.cwRaw || []);
  writeRaw(wb, 'CW Transactions', r.cwTxRaw || []);
  if (!wb.SheetNames.length) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([[]]), 'Empty');
  }
  return wb;
}

export function downloadDerivativeReconExport(recon) {
  const wb = buildDerivativeReconWorkbook(recon);
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `Derivative_Recon_${stamp}.xlsx`, { cellDates: true });
}
